import { Sparkles, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';

interface Props {
  startedAt: string;
  onResume: () => void;
  onDiscard: () => void;
}

export function DraftResumePrompt({ startedAt, onResume, onDiscard }: Props) {
  const { t } = useTranslation('avatars');
  const when = formatRelativeDate(startedAt, t);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80 px-4 backdrop-blur">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-2xl bg-bg-secondary p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/15">
            <Sparkles aria-hidden className="h-5 w-5 text-brand" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="text-base font-bold text-fg">{t('editor.draft.title')}</h2>
            <p className="text-xs text-fg-muted">
              {t('editor.draft.body', { when })}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={onResume}>{t('editor.draft.resume')}</Button>
          <button
            type="button"
            onClick={onDiscard}
            className={cn(
              'flex items-center justify-center gap-2 rounded-xl bg-bg-tertiary py-3 text-sm font-semibold text-fg-muted',
              'transition-opacity active:opacity-80',
            )}
          >
            <Trash2 aria-hidden className="h-4 w-4" />
            {t('editor.draft.discard')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRelativeDate(iso: string, t: TFunction): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const minutes = Math.round((now - then) / 60_000);
  if (minutes < 1) return t('editor.draft.justNow');
  if (minutes < 60) return t('editor.draft.minutesAgo', { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('editor.draft.hoursAgo', { n: hours });
  const days = Math.round(hours / 24);
  return t('editor.draft.daysAgo', { n: days });
}
